import { isPlatformBrowser, NgStyle, NgTemplateOutlet } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  PLATFORM_ID,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import type { Subscription } from 'rxjs';
import { trackByOptionProperty } from '../helpers/helpers';
import { IconColor, IconModule, IconSize } from '../icon';
import { textVariants } from '../text';
import { FontColor, FontWeight, TextSize } from '../text';
import type { ToggleButtonOptionContext } from './toggle-buttons.types';
import type { ToggleButtonsIconName } from './toggle-buttons.types';
import type { ToggleOption } from './toggle-buttons.types';
import {
  ToggleButtonsColor,
  ToggleButtonsSpacing,
  ToggleButtonsVariant,
} from './toggle-buttons.types';

@Component({
  standalone: true,
  imports: [IconModule, NgStyle, NgTemplateOutlet],
  selector: 'fl-toggle-buttons',
  template: `
    <div
      class="Container"
      role="listbox"
      [attr.data-equal-option-width]="equalOptionWidth"
      [attr.data-color]="color"
      [attr.data-variant]="variant"
      [attr.data-spacing]="spacing"
      [attr.aria-label]="label"
    >
      <div class="OptionsTrack">
        @for (
          option of options;
          track trackByOptionProperty(index, option);
          let index = $index;
          let isLast = $last
        ) {
          <div
            #toggleOptions
            role="option"
            tabindex=""
            class="Option"
            [class.mr-xxxsmall]="!equalOptionWidth && !isLast"
            [class.Selected]="control.value === option.option"
            [attr.aria-selected]="control.value === option.option"
            [attr.data-option-value]="option.option"
            [attr.data-color]="color"
            [attr.data-icons]="isIconOption(option)"
            [attr.data-full-width]="fullWidth"
            (click)="changeValue(option)"
          >
            @if (optionTemplate) {
              <ng-container
                [ngTemplateOutlet]="optionTemplate"
                [ngTemplateOutletContext]="{
                  $implicit: option,
                  selected: control.value === option.option,
                }"
              ></ng-container>
            } @else if (isIconOption(option)) {
              <fl-icon
                [name]="option.icon"
                [size]="iconSize"
                [color]="
                  control.value === option.option
                    ? color === ToggleButtonsColor.DARK
                      ? IconColor.LIGHT
                      : IconColor.FOREGROUND
                    : color === ToggleButtonsColor.DARK
                      ? IconColor.LIGHT
                      : IconColor.DARK
                "
                [useIconFont]="true"
              ></fl-icon>
            } @else if (isValueOption(option)) {
              <div class="DarkText">
                <p
                  [class]="
                    'ValueLabel ' +
                    (control.value === option.option
                      ? selectedTextCls
                      : unselectedTextCls)
                  "
                >
                  {{ option.value }}
                </p>
              </div>
            }
          </div>
        }
      </div>

      <div
        #buttonElement
        class="Button"
        [attr.data-color]="color"
        [ngStyle]="{
          'width.px': selectedOptionWidth,
          transform: buttonTransform,
        }"
      ></div>
    </div>
  `,
  styleUrls: ['./toggle-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleButtonsComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private changeDetectorRef = inject(ChangeDetectorRef);
  private time = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);

  IconColor = IconColor;
  ToggleButtonsColor = ToggleButtonsColor;
  trackByOptionProperty = trackByOptionProperty;

  @Input() label?: string;
  @Input() color = ToggleButtonsColor.LIGHT;
  @Input() variant = ToggleButtonsVariant.DEFAULT;
  @Input() control: FormControl<string>;
  @Input() options: readonly ToggleOption[] = [];
  @Input() spacing?: ToggleButtonsSpacing;
  @Input() optionTemplate?: TemplateRef<ToggleButtonOptionContext>;
  @Input() iconSize = IconSize.MID;
  @Input() textSize = TextSize.BODY_16;
  @Input() equalOptionWidth = false;

  @HostBinding('attr.data-full-width')
  @Input()
  fullWidth = false;

  private optionsSubscription?: Subscription;
  selectedOptionWidth = 0;
  buttonTransform = 'translateX(0)';

  selectedTextCls: string;
  unselectedTextCls: string;

  @ViewChild('buttonElement') buttonElementRef!: ElementRef<HTMLDivElement>;
  @ViewChildren('toggleOptions') optionElements!: QueryList<
    ElementRef<HTMLDivElement>
  >;

  private resizeObserver?: ResizeObserver;

  // Type guard for context
  static ngTemplateContextGuard(
    _dir: ToggleButtonsComponent,
    ctx: unknown,
  ): ctx is ToggleButtonOptionContext {
    return true;
  }

  // Type guards for template
  isIconOption(
    option: ToggleOption,
  ): option is { icon: ToggleButtonsIconName; option: string } {
    return 'icon' in option;
  }

  isValueOption(
    option: ToggleOption,
  ): option is { value: string; option: string } {
    return 'value' in option;
  }

  ngOnInit(): void {
    this.computeTextClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'textSize' in changes ||
      'color' in changes ||
      'control' in changes ||
      'variant' in changes
    ) {
      this.computeTextClasses();
    }
    if ('equalOptionWidth' in changes) {
      this.time.setTimeout(() => this.updateButtonTransform());
    }
    if ('control' in changes) {
      this.optionsSubscription?.unsubscribe();
      this.optionsSubscription = this.control.valueChanges.subscribe(() =>
        this.updateButtonTransform(),
      );
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupResizeObserver();
    }
    this.time.setTimeout(() => this.updateButtonTransform());
  }

  ngOnDestroy(): void {
    this.optionsSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  private getSelectedElement(): ElementRef<HTMLDivElement> | undefined {
    return this.optionElements.find(
      element =>
        element.nativeElement.getAttribute('data-option-value') ===
        this.control.value,
    );
  }

  private setupResizeObserver(): void {
    const selectedElement = this.getSelectedElement();

    if (selectedElement?.nativeElement.clientWidth === 0) {
      // Element is not yet visible or has no width
      this.resizeObserver = new ResizeObserver(() => {
        // Upon resize, update the width and perform any necessary operations
        this.time.setTimeout(() => this.updateButtonTransform());
      });

      this.resizeObserver.observe(selectedElement?.nativeElement);
    }
  }

  updateButtonTransform(): void {
    const buttonElement = this.buttonElementRef.nativeElement;
    const selectedElement = this.getSelectedElement();

    if (selectedElement) {
      this.selectedOptionWidth = selectedElement.nativeElement.clientWidth;
      const transformOffset =
        selectedElement.nativeElement.offsetLeft - buttonElement.offsetLeft;
      this.buttonTransform = `translateX(${transformOffset}px)`;

      this.changeDetectorRef.markForCheck();
    }
  }

  changeValue(option: ToggleOption): void {
    this.control.setValue(option.option);
  }

  private computeTextClasses(): void {
    let selectedColor: FontColor;
    let unselectedColor: FontColor;

    if (this.variant === ToggleButtonsVariant.PILL) {
      selectedColor = FontColor.INHERIT;
      unselectedColor = FontColor.INHERIT;
    } else {
      switch (this.color) {
        case ToggleButtonsColor.DARK:
          selectedColor = FontColor.LIGHT;
          unselectedColor = FontColor.LIGHT;
          break;
        case ToggleButtonsColor.TRANSPARENT:
          selectedColor = FontColor.INHERIT;
          unselectedColor = FontColor.FOREGROUND;
          break;
        default:
          selectedColor = FontColor.FOREGROUND;
          unselectedColor = FontColor.INHERIT;
          break;
      }
    }

    this.selectedTextCls = textVariants({
      color: selectedColor,
      size: this.textSize,
      weight: FontWeight.BOLD,
    });
    this.unselectedTextCls = textVariants({
      color: unselectedColor,
      size: this.textSize,
    });
  }
}
